<script setup lang="ts">
import SingleResult from "../SingleResult.vue";
import { runQuote } from "@/composables/runQuote";
import { computed, reactive, ref } from "vue";
import config from "@/config";
import { useToast } from "@/services/toast";
import AdjustQuote from "./AdjustQuote/AdjustQuote.vue";
import { AnnuitiesPremium } from "@/entities/premium";
import Modal from "../Modal.vue";
import QuoteSummary from "../QuoteSummary.vue";
import { useRoute, useRouter } from "vue-router";
import { ApplyPageStages } from "@/stores/application";
import { captureException } from "@sentry/vue";
import { useQuoteStore } from "@/stores/quotes";
import { ensureDefined } from "@/utils/types";
import ToastRenderer from "../ToastRenderer.vue";
import AmsHandoffQuestionsModal from "../AmsHandoffQuestionsModal.vue";
import { getSessionId, parseQueryQuoteNumber } from "@/utils";
import { useConfigStore } from "@/stores/config";
import { useGrowthBookFeatures } from "@/composables/growthbook";
import { sortedPremiums } from "@/utils/quote";
import { HandoffBanner, TopActionBar } from "./Components";
import Premiums from "./Premiums.vue";

const { toast } = useToast();
const router = useRouter();
const route = useRoute();
const quoteStore = useQuoteStore();
const configStore = useConfigStore();
const { redesign_lg_indicative_results202605 } = useGrowthBookFeatures();

export type Results = {
    openAccordion: number | null;
    showQuoteSummaryModal: boolean;
    showAmsHandoffQuestionsModal: boolean;
    adjustQuoteToggle: boolean;
};

const state: Results = reactive({
    openAccordion: null,
    showQuoteSummaryModal: false,
    showAmsHandoffQuestionsModal: false,
    adjustQuoteToggle: false,
});

const quote = computed(() => ensureDefined(quoteStore.quote));
const quoteNumber = computed(() => parseQueryQuoteNumber(route.query?.quotenumber) ?? "");
const requoteLoading = ref(false);

const hasAtLeastOneExpiredPremium = computed(() => quoteStore.quotations.some(
    (premium: AnnuitiesPremium) => "isExpired" in premium.policy && premium.policy.isExpired),
);

const hasAtLeastOneAMSQuote = computed(() => quoteStore.quotations.some(
    (premium: AnnuitiesPremium) => premium.quoteGeneratedBy === "AMS"),
);

function handleAccordionClick(premiumId: number) {
    state.openAccordion = state.openAccordion !== premiumId ? premiumId : null;
}

function handleApply(premium: AnnuitiesPremium) {
    router.push({
        name: ApplyPageStages[ApplyPageStages.PensionWise],
        query: { premiumId: premium.premiumId, quoteNumber: quoteNumber.value },
    });
}

async function requote() {
    requoteLoading.value = true;
    try {
        const quote = await runQuote(quoteNumber.value);
        router.push({
            name: "results",
            query: { quotenumber: quote.quoteNumber },
        });
    } catch (err) {
        captureException(err);
        toast({
            useUnsafeHTMLMessage: true,
            title: "We could not retrieve quotes at this time",
            message: `Please try again or call Annuity Ready on <a href='tel:08003265479'>0800 326 5479</a> Monday - Friday,
                    between 9-5, excluding Bank Holidays.`,
            timeout: config.toastTimeout,
        });
    } finally {
        requoteLoading.value = false;
    }
}
</script>

<template>
    <form id="resultsForm">
        <TopActionBar
            @edit-my-details="router.push({ path: '/', query: { quotenumber: quoteNumber } })"
            @show-quote-summary="state.showQuoteSummaryModal = true"
        />
        <HandoffBanner
            v-if="hasAtLeastOneAMSQuote"
            @ams-get-guaranteed-quotes="() => state.showAmsHandoffQuestionsModal = true"
        />
        <div class="row">
            <AdjustQuote
                :quote-number="quoteNumber"
                :is-ams-handoff-quote="hasAtLeastOneAMSQuote"
                :has-at-least-one-expired-premium="hasAtLeastOneExpiredPremium"
                :loading="requoteLoading"
                @requote="requote"
                @ams-get-guaranteed-quotes="() => state.showAmsHandoffQuestionsModal = true"
            />
            <Premiums
                v-if="redesign_lg_indicative_results202605"
                :is-extranet-handoff="configStore.extranetHandoff"
                :premiums="quoteStore.quotations"
                :payment-frequency="quote.product.paymentFrequency"
                :quote-number="quoteNumber"
                @apply="handleApply"
            />
            <!-- this can be removed entirely once split test complete -->
            <div
                v-else
                id="parameters"
                class="col-12 col-lg-9 mt-1"
            >
                <div id="results">
                    <div
                        v-if="configStore.extranetHandoff"
                        class="row"
                    >
                        <div
                            id="quote-pack-message"
                            class="col-lg-12 text-center h5 blue p-2"
                        >
                            <p>
                                <small>Continue with any provider to see the
                                    option to generate quote pack</small>
                            </p>
                        </div>
                    </div>
                    <div id="accordion">
                        <div class="row">
                            <template
                                v-for="quotation in sortedPremiums(
                                    quoteStore.quotations,
                                )"
                                :key="`${quotation.premiumId}-${quotation.company.slug}`"
                            >
                                <SingleResult
                                    :id="'result_' + quoteNumber + '_' + quotation.premiumId"
                                    :quotation="quotation"
                                    :quote="quote"
                                    :quote-number="quoteNumber"
                                    :is-open="state.openAccordion === quotation.premiumId"
                                    :show-more-info-button="true"
                                    @update:accordion="handleAccordionClick"
                                    @apply="handleApply"
                                    @re-quote="requote"
                                    @ams-get-guaranteed-quotes="() => state.showAmsHandoffQuestionsModal = true"
                                />
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </form>
    <Modal
        id="quote-summary-modal"
        open-focus="close-quote-summary-modal"
        close-focus="btn-quote-summary"
        header="Your quote summary"
        :show="state.showQuoteSummaryModal"
        @close="state.showQuoteSummaryModal = false"
    >
        <template #body>
            <QuoteSummary :quote="quote" />
        </template>
    </Modal>
    <AmsHandoffQuestionsModal
        v-model="state.showAmsHandoffQuestionsModal"
        @requote="requote"
    />
    <ToastRenderer />
    <img
        :src="`${config.quoteApiUrl}/v1/annuities/pixels/quote?ref=${quoteNumber}&sessionId=${getSessionId()}`"
        alt=""
    >
</template>
